import { Box, Grid } from '@ltvco/refresh-lib/theme';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  Control,
  UseFormTrigger,
  UseFormClearErrors,
} from '@ltvco/refresh-lib/vendors';
import React, { useCallback, useState } from 'react';

import { SearchInput } from '../SearchInput';
import { SelectInput } from '../SelectInput';

import {
  StyledSelectInputContainer,
  VehicleInputsErrorTextPositionWrapper,
  VehicleSearchFieldContainer,
} from './components';
import { getAbbrStatesAsArray, VehicleFormData } from './utils';

interface LicensePlateSearchInputProps {
  placeholderText: string;
  register: UseFormRegister<VehicleFormData>;
  errors: FieldErrors<VehicleFormData>;
  clearErrors: UseFormClearErrors<VehicleFormData>;
  setValue: UseFormSetValue<VehicleFormData>;
  control: Control<VehicleFormData>;
  trigger: UseFormTrigger<VehicleFormData>;
  value: string;
  stateValue: string;
  onSubmit?: () => void;
  isMobile?: boolean;
}

export const LicensePlateSearchInput = React.memo(
  ({
    placeholderText,
    register,
    errors,
    clearErrors,
    setValue,
    control,
    trigger,
    value,
    stateValue,
    onSubmit,
    isMobile = false,
  }: LicensePlateSearchInputProps) => {
    const states = getAbbrStatesAsArray();
    const registerReturn = register('licensePlate');
    const [licensePlateValue, setLicensePlateValue] = useState(value);

    const handleClear = () => {
      setValue('licensePlate', '');
      clearErrors('licensePlate');
      setLicensePlateValue('');
    };

    const handleCustomChange = (
      e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      // eslint-disable-next-line no-unused-vars
      field: { onChange: (value: string) => void }
    ) => {
      const sanitizedValue = e.target.value.toUpperCase();
      field.onChange(sanitizedValue);
      setValue('licensePlate', sanitizedValue);
      setLicensePlateValue(sanitizedValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onSubmit) {
        e.preventDefault();
        trigger().then((isValid) => {
          if (isValid) {
            onSubmit();
          }
        });
      }
    };

    const handleSearchTypeChange = (value: string) => {
      setValue('state', value === 'All' ? '' : value);
      clearErrors('state');
    };

    const options = states.map((state) => ({
      label: state === 'All' ? 'State' : state,
      value: state,
    }));

    const SearchInputComponent = useCallback(() => {
      return (
        <VehicleInputsErrorTextPositionWrapper sx={{ width: '100%' }}>
          <SearchInput
            className="license-plate-search-input"
            registerReturn={registerReturn}
            label=""
            placeholder={placeholderText}
            error={errors.licensePlate}
            clearErrors={clearErrors as unknown as () => void}
            onClear={handleClear}
            value={licensePlateValue}
            fieldName="licensePlate"
            control={control as unknown as Control}
            inputProps={{
              maxLength: 10,
              onKeyDown: handleKeyDown,
            }}
            customOnChange={handleCustomChange}
            autoFocus
          />
        </VehicleInputsErrorTextPositionWrapper>
      );
    }, [
      registerReturn,
      errors.licensePlate,
      clearErrors,
      licensePlateValue,
      handleClear,
      handleKeyDown,
      handleCustomChange,
      placeholderText,
    ]);

    const stateSelectJsx = (
      <StyledSelectInputContainer
        sx={{
          '& .MuiFormControl-root': {
            minWidth: '108px',
          },
        }}
      >
        <SelectInput
          options={options}
          value={stateValue}
          onChange={handleSearchTypeChange}
          name="state"
          control={control}
          defaultOptionValue={'All'}
        />
      </StyledSelectInputContainer>
    );

    const renderMobileInputs = () => {
      const errorClassName = Object.keys(errors).length > 0 ? 'error' : '';

      return (
        <Grid container spacing={2} gap={1} flexDirection={'column'}>
          <Grid size={{ xs: 12 }}>
            <VehicleSearchFieldContainer className={errorClassName}>
              {<SearchInputComponent />}
            </VehicleSearchFieldContainer>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <VehicleSearchFieldContainer>
              {stateSelectJsx}
            </VehicleSearchFieldContainer>
          </Grid>
        </Grid>
      );
    };

    const renderDesktopInputs = () => {
      return (
        <Grid container spacing={1} gap={0} flexDirection={'row'}>
          <Grid size={{ xs: 12, sm: 9 }}>{<SearchInputComponent />}</Grid>
          <Grid size={{ xs: 12, sm: 3 }}>{stateSelectJsx}</Grid>
        </Grid>
      );
    };

    return (
      <Box sx={{ width: '100%' }}>
        {isMobile ? renderMobileInputs() : renderDesktopInputs()}
      </Box>
    );
  }
);
